import { CustomizationOptionDto } from '../models/customization/customization-option.dto';
import { String } from '../../_core/utils/utils.string';
import { Evaluate } from '../../_core/utils/utils.evaluate';
import { CustomizationSelectDto } from '../models/customization/customization.dto';
import { MergeTags } from '../../_core/utils/utils.merge-tags';
import { ObjectUtils } from '../../_core/utils/utils.object';

export class CustomizationOptionUtils {
	public static create(option: Partial<CustomizationOptionDto>): CustomizationOptionDto {
		return {
			id: option.id || new Date().getTime().toString() + Math.random().toString(36).substring(2, 15),
			name: option.name,
			label: option.label || 'Label',
			value: option.value || String.slugify(option.label),
			layer: option.layer,
			layers: option.layers || [],
			styles: option.styles || '',
			metadata: option.metadata || {},
			creativeUnits: option.creativeUnits || [],
			variables: option.variables,
			visibilityConditions: option.visibilityConditions || null,
			resetStylesToDefault: option.resetStylesToDefault,
			transformUnit: option.transformUnit ? { ...option.transformUnit } : undefined
		};
	}

	/**
	 * Whether a dynamic link labelKey is a merge-tag template (e.g. "{{customFields.modelName}} - {{customFields.trim}}")
	 * rather than a plain dot path. Plain paths are the common case and skip merge-tag evaluation entirely.
	 */
	public static isLabelTemplate(labelKey: string | undefined): boolean {
		return typeof labelKey === 'string' && labelKey.includes('{{');
	}

	/**
	 * Resolve a dynamic option label from a data source item.
	 * A plain labelKey is read as a dot path; a template labelKey is run through merge tags
	 * (paths, `|| 'default'`, `fn(...)`, `@script(...)`) against the item plus `variables`. Server paths pass package
	 * variables while the editor passes live selection variables, so templates should reference item fields only.
	 * Returns undefined when the label resolves empty so callers can apply their own fallback.
	 */
	public static resolveDynamicLabel(labelKey: string | undefined, item: any, variables?: { [key: string]: any }): string | undefined {
		if (!labelKey) {
			return undefined;
		}

		if (!CustomizationOptionUtils.isLabelTemplate(labelKey)) {
			return ObjectUtils.resolveDotNotationPath(labelKey, item);
		}

		let label = MergeTags.applyMergeTagsToString(labelKey, { ...item, variables: variables || {} });

		// Path values come back JSON-escaped (merge tags target JSON strings); undo that for display.
		if (typeof label === 'string' && label.includes('\\')) {
			try {
				label = JSON.parse(`"${label}"`);
			} catch {
				// leave as-is
			}
		}

		return typeof label === 'string' && label.trim() ? label.trim() : undefined;
	}

	/**
	 * Resolve a dynamic option label, preferring the locale-specific labelKey when one is configured.
	 * `resolvedLocaleLabelKey` is the locale labelKey after the mapping merge-tag pass; a template locale
	 * labelKey is taken from the raw mapping instead, since that pass would already have flattened it.
	 */
	public static resolveLocalizedDynamicLabel(
		dynamicLink: { labelKey?: string; mapping?: any } | undefined,
		item: any,
		variables: { [key: string]: any } | undefined,
		localeCode: string | null | undefined,
		resolvedLocaleLabelKey?: string
	): string | undefined {
		const label = CustomizationOptionUtils.resolveDynamicLabel(dynamicLink?.labelKey, item, variables);

		// A locale labelKey may use merge tags to build a path (e.g. `customFields.name_{{variables.lang}}`), so the
		// merge-resolved value is tried as a path first; only when that finds nothing is the raw value a label template.
		const pathLabel =
			resolvedLocaleLabelKey && !CustomizationOptionUtils.isLabelTemplate(resolvedLocaleLabelKey)
				? ObjectUtils.resolveDotNotationPath(resolvedLocaleLabelKey, item)
				: undefined;
		const rawLocaleLabelKey = localeCode ? dynamicLink?.mapping?.locales?.['labelKey']?.[localeCode] : undefined;
		const templateLabel =
			!pathLabel && CustomizationOptionUtils.isLabelTemplate(rawLocaleLabelKey)
				? CustomizationOptionUtils.resolveDynamicLabel(rawLocaleLabelKey, item, variables)
				: undefined;

		return pathLabel || templateLabel || label;
	}

	/**
	 * 1. Reparse mapping and visibility conditions for options
	 * 2. Filter out options that are not supposed to be visible
	 * @param customization
	 * @param options
	 * @param mergeTagState
	 */
	public static processOptions(
		customization: CustomizationSelectDto,
		options: CustomizationOptionDto[],
		mergeTagState: { [key: string]: any }
	) {
		const reparsedOptions = CustomizationOptionUtils.reparseOptions(customization, options, mergeTagState);
		return CustomizationOptionUtils.getVisibleOptions(reparsedOptions, mergeTagState);
	}

	/**
	 * Evaluate the visibility conditions for each option and filter out the ones that are not supposed to be visible.
	 * @param options
	 * @param mergeState
	 */
	public static getVisibleOptions(options: CustomizationOptionDto[], mergeState: any): CustomizationOptionDto[] {
		return options?.filter(option => {
			if (option.visibilityConditions) {
				// console.log('Evaluating visibility conditions', option.visibilityConditions, mergeState);
				return Evaluate.evaluateMultiple(option.visibilityConditions, {
					...mergeState,
					option
				});
			} else {
				return true;
			}
		});
	}

	/**
	 * Compare two options to see if they should be treated as equal.
	 * @param a
	 * @param b
	 * @param strict If true, compare all properties. If false, compare only value and label.
	 */
	public static areOptionsEqual(a: CustomizationOptionDto, b: CustomizationOptionDto, strict: boolean = true) {
		if (!a && !b) {
			return true;
		}
		if (!a || !b) {
			return false;
		}

		if (a.value !== b.value || a.label !== b.label) {
			return false;
		}

		if (strict) {
			if (a.name !== b.name) {
				return false;
			}

			if (a.layers?.length !== b.layers?.length) {
				return false;
			}

			// remove the ID from the comparison as it differs between the same option saved and newly loaded
			const valueLayers = { ...a.layers, id: undefined };
			const optionLayers = { ...b.layers, id: undefined };

			if (JSON.stringify(valueLayers) !== JSON.stringify(optionLayers)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Reparse the mapping and visibility conditions for each option using the merge tag state.
	 * @param customization
	 * @param options
	 * @param mergeTagState
	 */
	public static reparseOptions(
		customization: CustomizationSelectDto,
		options: CustomizationOptionDto[],
		mergeTagState: { [key: string]: any }
	) {
		return options?.map(option => {
			let mapping: any = {};
			try {
				mapping = JSON.parse(
					MergeTags.applyMergeTagsToString(JSON.stringify(customization.dynamicLink.mapping), {
						...option.metadata,
						...mergeTagState
					})
				);
			} catch (e) {
				console.warn('Unable to parse dynamic link mapping', e);
			}

			return CustomizationOptionUtils.create({
				...option,
				id: option.id,
				label: option.label,
				...mapping,
				visibilityConditions: mapping.visibilityConditions,
				transformUnit: mapping.transformUnit ?? option.transformUnit
			});
		});
	}
}
